import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Gift, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReferralTable, NotificationList } from './ReferEarnParts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
  </div>
);

export default function ReferEarnPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    axios.get(`${API}/referral-rewards/my`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Could not load Refer & Earn'));
  }, []);
  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error('Copy failed'); }
  };

  if (err) return <div className="p-8 text-sm text-rose-700" data-testid="refer-error">{err}</div>;
  if (!d) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  const s = d.summary;
  return (
    <div className="p-6 max-w-6xl space-y-6" data-testid="refer-earn-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><Gift className="w-7 h-7 text-orange-500" /> Refer &amp; Earn</h1>
        <p className="text-sm text-slate-500 mt-1">Refer another clinic: you earn 1 free month (2 for a Chain clinic), and they get 1 free month.</p>
      </div>
      {!d.eligible ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900" data-testid="refer-not-eligible">
          Your referral code is created once your clinic has an active paid subscription.
        </div>
      ) : (
        <div className="bg-[#0F1D3A] text-white rounded-2xl p-6 flex flex-wrap gap-6 items-center" data-testid="refer-code-card">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold">Your referral code</div>
            <div className="text-3xl font-mono font-bold mt-1" data-testid="refer-code">{d.code}</div>
            <div className="text-[13px] text-slate-300 mt-2 break-all" data-testid="refer-link">{d.link}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => copy(d.code, 'Code')} data-testid="refer-copy-code" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors"><Copy className="w-4 h-4" /> Copy code</button>
            <button onClick={() => copy(d.link, 'Link')} data-testid="refer-copy-link" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors"><Copy className="w-4 h-4" /> Copy link</button>
            <a href={`https://wa.me/?text=${encodeURIComponent(d.whatsapp_text)}`} target="_blank" rel="noopener noreferrer" data-testid="refer-whatsapp"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-sm font-semibold transition-colors"><MessageCircle className="w-4 h-4" /> Share on WhatsApp</a>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Referrals sent" value={s.sent} testid="refer-stat-sent" />
        <Stat label="Pending" value={s.pending} testid="refer-stat-pending" />
        <Stat label="Qualified" value={s.qualified} testid="refer-stat-qualified" />
        <Stat label="Months earned" value={s.months_earned} testid="refer-stat-months" />
        <Stat label="Current renewal date" value={fmt(s.renewal_date)} testid="refer-stat-renewal" />
      </div>
      {d.entitlements.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-900" data-testid="refer-entitlements">
          {d.entitlements.map((e) => <div key={e.entry_id}><b>Milestone unlocked:</b> {e.entitlement} — activation coming soon ({e.note}).</div>)}
        </div>
      )}
      <ReferralTable rows={d.referrals} />
      <NotificationList items={d.notifications} />
      <Link to="/referral-terms" className="inline-block text-sm text-indigo-600 hover:underline" data-testid="refer-terms-link">Referral program terms →</Link>
    </div>
  );
}
